import { forwardRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { t } from 'i18next';

interface MonitoringShovelfulsChartProps {
    data: number[];
    timestamps: string[];
}

export const MonitoringShovelfulsChart = forwardRef<ReactECharts, MonitoringShovelfulsChartProps>((props, ref) => {
    const categories = props.timestamps && props.timestamps.length > 0
        ? props.timestamps
        : props.data.map((_, index) => `${t("Sample")} ${index + 1}`);

    // Calcular promedio, máximo y mínimo
    const avg = props.data.length > 0
        ? props.data.reduce((sum, val) => sum + val, 0) / props.data.length
        : 0;
    const max = props.data.length > 0 ? Math.max(...props.data) : 0;
    const min = props.data.length > 0 ? Math.min(...props.data) : 0;

    const options = {
        title: {
            text: t('Shovelfuls per minute'),
            left: 'center'
        },
        tooltip: {
            trigger: 'axis',
            formatter: (params: any) => {
                let result = `${params[0].name}<br/>`;
                params.forEach((param: any) => {
                    if (param.seriesName === t('Strokes/min')) {
                        result += `${param.marker}${param.seriesName}: ${parseFloat(param.data).toFixed(1)}<br/>`;
                    } else {
                        result += `${param.marker}${param.seriesName}: ${parseFloat(param.data).toFixed(1)}<br/>`;
                    }
                });
                return result;
            }
        },
        legend: {
            data: [t('Strokes/min'), t('Average'), t('Maximum'), t('Minimum')],
            top: '10%'
        },
        xAxis: {
            type: 'category',
            data: categories
        },
        yAxis: {
            type: 'value',
            name: t('shoverfuls') + '/min',
            axisLabel: {
                formatter: (value: number) => {
                    // Marcar el promedio, máximo y mínimo con colores
                    const formattedValue = value.toFixed(1);
                    if (Math.abs(value - avg) < 0.5) {
                        return `{avg|${formattedValue}}`;
                    } else if (Math.abs(value - max) < 0.5) {
                        return `{max|${formattedValue}}`;
                    } else if (Math.abs(value - min) < 0.5) {
                        return `{min|${formattedValue}}`;
                    }
                    return formattedValue;
                },
                rich: {
                    avg: {
                        color: '#91cc75',
                        fontWeight: 'bold'
                    },
                    max: {
                        color: '#ee6666',
                        fontWeight: 'bold'
                    },
                    min: {
                        color: '#fac858',
                        fontWeight: 'bold'
                    }
                }
            }
        },
        series: [
            {
                name: t('Strokes/min'),
                data: props.data,
                type: 'line',
                smooth: true,
                color: '#5470c6',
                label: {
                    show: true,
                    position: 'top',
                    formatter: (params: any) => parseFloat(params.data).toFixed(1)
                },
                lineStyle: {
                    width: 3
                },
                itemStyle: {
                    borderWidth: 3
                }
            },
            {
                name: t('Average'),
                data: props.data.map(() => avg),
                type: 'line',
                color: '#91cc75',
                lineStyle: {
                    type: 'dashed',
                    width: 2
                },
                symbol: 'none'
            },
            {
                name: t('Maximum'),
                data: props.data.map(() => max),
                type: 'line',
                color: '#ee6666',
                lineStyle: {
                    type: 'dashed',
                    width: 2
                },
                symbol: 'none'
            },
            {
                name: t('Minimum'),
                data: props.data.map(() => min),
                type: 'line',
                color: '#fac858',
                lineStyle: {
                    type: 'dashed',
                    width: 2
                },
                symbol: 'none'
            }
        ],
        grid: {
            left: '10%',
            right: '10%',
            bottom: '10%',
            top: '20%'
        }
    };

    return (
        <div className="monitoring-chart">
            <ReactECharts ref={ref} option={options} style={{ height: '300px' }} />
        </div>
    );
});
